import { yupResolver } from "@hookform/resolvers/yup";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useProjectTagValidationComparisonData,
} from "~/modules/project/api/projectTag/hooks/useProjectTagValidationComparisonData.ts";
import { createProjectTag } from "~/modules/project/api/projectTag/projectTagApiDispatchers.ts";
import type { ProjectTagTypeId } from "~/modules/project/api/projectTag/projectTagTypes.ts";
import {
	getProjectTagTypeDisplayNameById,
} from "~/modules/project/utils/projectTagTypeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

interface CreateDeliverableTagFormData {
	displayName: string;
}

type CreateDeliverableTagFormProps = {
	onSuccess: () => void;
	onCancel: () => void;
	tagTypeId: ProjectTagTypeId;
};

const CreateProjectTagForm: FunctionComponent<CreateDeliverableTagFormProps> = ({
	onSuccess,
	onCancel,
	tagTypeId
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const validationComparisonData = useProjectTagValidationComparisonData(tagTypeId);
	const schema = useMemo(() => {
		return yup.object({
			displayName: yup.string().required().unique(validationComparisonData),
		});
	}, [validationComparisonData]);

	const defaultValues = useMemo(() => {
		return {};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<CreateDeliverableTagFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateDeliverableTagFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: CreateDeliverableTagFormData) => {
		try {
			setIsBusy(true);
			await createProjectTag({displayName:data.displayName, tagTypeId});
			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			console.log(apiError);
			setServerErrorMessage("Es ist ein unerwarteter Fehler aufgetreten.");
		}
	}, [onSuccess, tagTypeId]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<Headline type="h4"
						  color="muted">Neuer {getProjectTagTypeDisplayNameById(tagTypeId)} Tag</Headline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Speichern fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<Input control={control}
					   name="displayName"
					   label="Name"
				/>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateProjectTagForm;
